import { useCallback, useEffect, useRef, useState } from "react";

/** Carousel arrows reflect actual scroll position, not decorative buttons. */
export function useRailScroll() {
  const ref = useRef(null);
  const [bounds, setBounds] = useState({ canPrev: false, canNext: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      const next = { canPrev: el.scrollLeft > 3, canNext: el.scrollLeft < max - 3 };
      setBounds((prior) => prior.canPrev === next.canPrev && prior.canNext === next.canNext ? prior : next);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    resize?.observe(el);
    const mutation = typeof MutationObserver === "undefined" ? null : new MutationObserver(update);
    mutation?.observe(el, { childList: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      resize?.disconnect();
      mutation?.disconnect();
    };
  }, []);

  const nudge = useCallback((dir) => {
    const el = ref.current;
    if (!el) return;
    const first = el.firstElementChild;
    const second = el.children[1];
    const gap = first && second ? Math.max(0, second.offsetLeft - first.offsetLeft - first.offsetWidth) : 12;
    const distance = first ? first.getBoundingClientRect().width + gap : el.clientWidth * .75;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    el.scrollBy({ left: dir * distance, behavior: reduced ? "auto" : "smooth" });
  }, []);

  return { ref, nudge, ...bounds };
}
